/*
  DefaultForm.jsx
*/

// Npm.
import React, { useMemo } from 'react';

// Lib.
import getUniqueNodeId from '../../../utils/getUniqueNodeId';
import getDefaultScenario from '../../../utils/getDefaultScenario';
import botechToGraphData from '../../../utils/botechToGraphData';
import expandAllNodes from '../../../utils/expandAllNodes';
import collapseAllSingleParentNodes from '../../../utils/collapseAllSingleParentNodes';

// Components.
import Button from '../../Button';
import Divider from '../../Divider';

// Hooks.
import useReadOnly from '../../../hooks/useReadOnly';

// Makes.
import { makeStyles } from '../../../makes/makeStyles';

// Styles.
const useStyles = makeStyles(theme => ({
  helpText: {
    margin: theme.space(4, 0),
    textAlign: 'center',
    color: theme.color_text_disabled,
    fontStyle: 'italic',
  },
  divider: {
    margin: theme.space(4, 0),
  },
  actionsContainer: {
    '& > div': {
      marginTop: theme.space(1),
      '&:first-of-type': {
        marginTop: 0,
      }
    }
  },
}));

/**
 * Node editing form.
 */
export default function NodeForm({
  className,
  graphData,
  onChangeGraphData,
  onChangeSelectedNode,
  disabled,
  ...rest
}) {

  const classes = useStyles();
  const readOnly = useReadOnly();
  const canExpand = graphData.nodes.some(node => Boolean(node._botech?.collapsed));
  const canCollapse = graphData.nodes.length > 0;
  const canClear = Boolean(graphData.nodes.length) || Boolean(graphData.links.length);

  // Handlers.

  function handleClickCreate() {

    const newNode = { id: getUniqueNodeId(graphData) };

    if (onChangeGraphData) {
      onChangeGraphData(data => ({
        ...data,
        nodes: data.nodes.concat(newNode),
      }));
    }

    if (onChangeSelectedNode) {
      onChangeSelectedNode(newNode);
    }

  }

  function handleExpandGraph() {
    if (onChangeGraphData) {
      onChangeGraphData(data => expandAllNodes(data));
    }
  }

  function handleCollapseGraph() {
    if (onChangeGraphData) {
      onChangeGraphData(data => collapseAllSingleParentNodes(data));
    }
  }

  // Handlers for clearing the graph data.

  function handleClearGraph() {
    if (window.confirm('Clear all graph contents?')) {
      onChangeGraphData(null);
    }
  }

  function handleResetGraph() {
    if (window.confirm('Reset graph to the default graph?')) {
      onChangeGraphData(botechToGraphData(getDefaultScenario()));
    }
  }

  return (
    <div>

      <div className={`${classes.helpText} body2`}>
        { readOnly ? "Click a Node or Link to view it's properties" : 'Click a Node or Link to begin editing' }
      </div>

      {
        !readOnly &&
        <div>
          <Button onClick={handleClickCreate} disabled={disabled} fullWidth>
            Create Node
          </Button>
        </div>
      }

      {
        !readOnly &&
        <div className={classes.divider}>
          <Divider />
        </div>
      }

      <div className={classes.actionsContainer}>
        <div>
          <Button onClick={handleExpandGraph} disabled={disabled || !canExpand} fullWidth>
            Expand All
          </Button>
        </div>
        <div>
          <Button onClick={handleCollapseGraph} disabled={disabled || !canCollapse} fullWidth>
            Collapse All
          </Button>
        </div>
        {
          !readOnly &&
          <div>
            <Button onClick={handleClearGraph} disabled={disabled || !canClear} fullWidth>
              Clear Graph
            </Button>
          </div>
        }
        {
          !readOnly &&
          <div>
            <Button onClick={handleResetGraph} disabled={disabled} fullWidth>
              Load Default Graph
            </Button>
          </div>
        }
      </div>

    </div>
  );
}
